import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { Observable, map, startWith, take, forkJoin, catchError, of } from 'rxjs';

import { UserFieldEntityTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import UserFieldCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-catalog.entity';
import { UpdateFieldPayload } from '../company-form-config/interfaces/field-catalog.interface';
import { HasPermissionPipe, PermissionDirective, PermissionService } from "app/core/permission";
import { COMPANY_USER_FIELDS_CONFIG_PERMISSIONS } from "app/common/constants/permission";
import { UserFieldConfigAttributes } from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-config/user-field-config.entity';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { BooleanCellComponent } from 'app/modules/components/system/configuration/temporary-password-render-fields/boolean-cell.component';
import { SelectSchemaComponent } from 'app/shared/components/select-schema/select-schema.component';
import { ColDef, GridConfig, SmartGridComponent } from 'app/shared/components/smart-grid';
import { fieldTypeOptionsForm, ICON_OPTIONS } from 'app/shared/constants/lists.constant';
import { CompanyFieldCatalogService } from '../company-form-config/company-form-config.service';
import { AttachmentConfigDialogComponent } from '../company-form-config/modals-config/attachment-config-dialog.component';
import { ConfirmDialogComponent } from '../company-form-config/modals-config/confirm-dialog.component';
import {
  USER_FIELD_GROUP_OPTIONS,
  normalizeUserFieldGroupValue,
} from './user-field-group.constants';
import { UF, ufFieldHandlers } from './user-field-name.constants';
import { FieldActionsCellComponent } from './modals-config/user-sub-fields-config/field-actions.component';
import { UserSubfieldsConfigComponent } from './modals-config/user-sub-fields-config/user-sub-fields-config.component';
import { ValuesModalComponent } from './values-dialog/values-dialog.component';
import { FieldGroupsDialogComponent } from '../field-groups-dialog/field-groups-dialog.component';
import { FieldGroup } from '../field-groups-dialog/field-group.interface';
import {
  fillNextFreePosition,
  normalizeFieldPositions,
  normalizeGroupPositions,
  normalizeIconColor,
} from '../field-groups-dialog/field-groups.util';


@Component({
  selector: 'app-user-fields-catalog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    ToggleOptionComponent,
    PermissionDirective,
    HasPermissionPipe,
    FormSectionComponent,
    MatTooltipModule,
    MatMenuModule,
    SmartGridComponent,
    SelectSchemaComponent
  ],
  templateUrl: './user-fields-catalog.component.html',
})
export class UserFieldsCatalogComponent implements OnInit {
  readonly userFieldsConfigPermissions = COMPANY_USER_FIELDS_CONFIG_PERMISSIONS;



  get currentGroups(): FieldGroup[] {
    const currentEntity = this.entities[this.selectedEntityIndex];
    return this.formConfigs[currentEntity]?.get('groups')?.value ?? [];
  }

  groupName(groupId: string): string {
    return this.currentGroups.find((group) => group._id === groupId)?.name ?? 'Sin Grupo';
  }
  readonly maxFieldsPerEntity = 10;
  readonly availableIcons = ICON_OPTIONS;
  readonly isErpIntegrationActive: boolean;
  private readonly autoSavingStatus = new Set<UserFieldEntityTypeEnum>();
  private readonly savingCatalog = new Set<UserFieldEntityTypeEnum>();

  viewMode: 'list' | 'edit' = 'list';
  editingFieldIndex: number | null = null;
  gridData: any[] = [];
  isMobileMenuOpen = false;

  formConfigs: Record<UserFieldEntityTypeEnum, FormGroup> = {} as any;
  entities: UserFieldEntityTypeEnum[] = Object.values(UserFieldEntityTypeEnum);
  selectedEntityIndex = 0;

  searchControl = new FormControl('');
  iconSearchControl = new FormControl('');
  filteredIcons$!: Observable<string[]>;
  filteredEntities$: Observable<UserFieldEntityTypeEnum[]>;

  readonly entityLabels: Partial<Record<UserFieldEntityTypeEnum, string>> = {
    [UserFieldEntityTypeEnum.BUSINESS_PARTNER]: 'Socios de Negocio',
    [UserFieldEntityTypeEnum.BP_DISTRIBUTION_CHAIN]: 'Cadena de Distribución de socio',
    [UserFieldEntityTypeEnum.MATERIAL]: 'Materiales',
    [UserFieldEntityTypeEnum.MATERIAL_DISTRIBUTION_CHAIN]: 'Cadena de Distribución de material',
    [UserFieldEntityTypeEnum.DOCUMENT_CLASS]: 'Clases de documento',
    [UserFieldEntityTypeEnum.SUB_ROUTE]: 'Rutas',
    [UserFieldEntityTypeEnum.SALES_OFFICE]: 'Oficinas de venta',
    [UserFieldEntityTypeEnum.PLANTS]: 'Plantas',
    [UserFieldEntityTypeEnum.WAREHOUSE]: 'Almacenes',
    [UserFieldEntityTypeEnum.DISTRIBUTION_CHANNEL]: 'Canales de distribución',
    [UserFieldEntityTypeEnum.DIVISION]: 'Divisiones',
    [UserFieldEntityTypeEnum.DISTRIBUTION_CHAIN]: 'Cadenas de distribución',
  };

  readonly fieldTypeOptions = fieldTypeOptionsForm;
  readonly uf = UF;
  readonly ufField = ufFieldHandlers;

  @ViewChild('addModal') addModal!: TemplateRef<any>;
  private dialogRefAdd!: MatDialogRef<any>;
  private isNewField = false;

  columnDefs: ColDef<any>[] = [];

  gridConfig: GridConfig<any> = {
    emptyState: { message: 'No hay campos configurados para esta entidad', showIcon: true },
    pagination: { enabled: true, pageSize: 10, showPaginator: true },
    styling: { density: 'comfortable', fontSize: '12px' },
  };

  constructor(
    private fb: FormBuilder,
    private companyFieldCatalogService: CompanyFieldCatalogService,
    private handlerMessage: HandlerMessageService,
    public dialogRef: MatDialogRef<UserFieldsCatalogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { companyId: string; erpActive?: boolean }
  ) {
    this.isErpIntegrationActive = Boolean(data?.erpActive);
  }

  ngOnInit(): void {
    this.buildColumnDefs();
    this.entities.forEach((entity) => {
      this.formConfigs[entity] = this.createEntityForm(entity);
      this.setupStatusAutoSave(entity);
    });
    this.setupSearch();
    this.applyStatusToggleState();
    this.loadAllConfigs();

    this.filteredIcons$ = this.iconSearchControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const search = (value || '').toLowerCase();
        return this.availableIcons.filter((ic) => ic.toLowerCase().includes(search));
      })
    );
  }

  private buildColumnDefs(): void {
    const canEdit = true;
    const canDelete = true;
    this.columnDefs = [
      { field: 'fieldName', headerName: 'Nombre DB', sortable: true, width: 150, pinned: 'left', lockPinned: true },
      { field: 'labelMaster', headerName: 'Etiqueta', sortable: true, width: 200 },
      { field: 'fieldType', headerName: 'Tipo', sortable: true, width: 120 },
      { field: 'group', headerName: 'Grupo', sortable: true, width: 200 },
      { field: 'position', headerName: 'Posición', sortable: true, width: 100 },
      { field: 'isVisible', headerName: 'Visible', width: 100, cellRenderer: BooleanCellComponent },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: 120,
        sortable: false,
        pinned: 'right',
        lockPinned: true,
        cellRenderer: FieldActionsCellComponent,
        cellRendererParams: {
          onClick: (params: any) => this.editField(params.data.index),
          onDelete: (params: any) => this.deleteField(params.data.index),
          canEdit,
          canDelete,
        },
      },
    ];
  }

  /** Sin documento de catálogo: CREATE; con _id: UPDATE. */
  private applyStatusToggleState(): void {
    this.entities.forEach((entity) => {
      const form = this.formConfigs[entity];
      const catalogId = form?.get('_id')?.value as string | null | undefined;
      const canToggle = true;
      const ctrl = form?.get('statusId');
      if (canToggle) {
        ctrl?.enable({ emitEvent: false });
      } else {
        ctrl?.disable({ emitEvent: false });
      }
    });
  }

  private setupSearch() {
    this.filteredEntities$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
  }

  private _filter(value: string): UserFieldEntityTypeEnum[] {
    const filterValue = value.toLowerCase();
    return this.entities.filter((entity) =>
      this.getEntityLabel(entity).toLowerCase().includes(filterValue)
    );
  }

  updateGridData() {
    const entityType = this.entities[this.selectedEntityIndex];
    const fields = this.getFieldsArray(entityType).getRawValue();
    this.gridData = fields.map((f, index) => ({
      ...f,
      index,
      group: this.groupName(f.groupId),
      labelMaster: f.fieldLabel?.master || 'Sin etiqueta',
    }));
  }

  onEntitySelect(entity: UserFieldEntityTypeEnum) {
    this.selectedEntityIndex = this.entities.indexOf(entity);
    this.updateGridData();
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  private createEntityForm(entityType: UserFieldEntityTypeEnum): FormGroup {
    return this.fb.group({
      _id: [null],
      entityType: [entityType, [Validators.required]],
      statusId: [0, [Validators.required]],
      companyId: [this.data.companyId, [Validators.required]],
      fields: this.fb.array([]),
      groups: [[]],
    });
  }

  private setupStatusAutoSave(entityType: UserFieldEntityTypeEnum) {
    const statusControl = this.formConfigs[entityType]?.get('statusId');
    statusControl?.valueChanges.subscribe(() => this.saveStatusConfig(entityType));
  }

  loadAllConfigs() {
    const requests = this.entities.map((entityType) =>
      this.companyFieldCatalogService.getFieldsMaster(this.data.companyId, entityType).pipe(
        take(1),
        // If 404 (not found), return null so forkJoin doesn't fail
        catchError((err) => {
          if (err.status !== 404) {
            this.handlerMessage.error({ message: err.message });
          }
          return of(null);
        })
      )
    );

    forkJoin(requests).subscribe({
      next: (responses) => {
        // Flatten the data from successful responses
        const validData = responses
          .filter((res) => res && res.data)
          .map((res) => res!.data)
          .reduce((acc, val) => acc.concat(val), []) as UserFieldCatalogAttributes[];
        
        const catalogs = this.companyFieldCatalogService.indexUserFieldCatalogs(validData);

        this.entities.forEach((entityType, idx) => {
          const response = catalogs.get(entityType);
          const fieldsArray = this.getFieldsArray(entityType);
          fieldsArray.clear();
          this.formConfigs[entityType].patchValue(
            {
              _id: response?._id || null,
              statusId: response?.statusId === 1,
              groups: this.normalizeGroups(response?.groups),
            },
            { emitEvent: false }
          );
          if (response?.fields?.length) {
            response.fields.forEach((field) =>
              fieldsArray.push(this.createFieldGroup(field))
            );
            normalizeFieldPositions(fieldsArray);
          }
          if (idx === this.selectedEntityIndex) this.updateGridData();
        });
        this.applyStatusToggleState();
      },
      error: (err) => {
        this.handlerMessage.error({ message: 'Error loading configurations' });
      }
    });
  }

  /** POST de catálogo nuevo requiere CREATE; PATCH de existente requiere UPDATE. */
  private assertCanPersistCatalog(_id: string | null | undefined): boolean {
    const persistUpdate = !!_id;
    const ok = true;
    if (!ok) {
      this.handlerMessage.error({
        message: persistUpdate
          ? 'No tienes permiso para actualizar la configuración de campos de usuario.'
          : 'No tienes permiso para crear la configuración de campos de usuario.',
      });
    }
    return ok;
  }

  createFieldGroup(field: Partial<UserFieldConfigAttributes> = {}): FormGroup {
    const reverseTypeMap: Record<string, string> = {
      string: 'text',
      number: 'number',
      boolean: 'checkbox',
      date: 'date',
      array: 'select'
    };
    
    // If fieldType is missing but dataType is present (backend drops fieldType), reverse map it
    let type = field?.fieldType;
    if (!type && field?.dataType) {
      type = reverseTypeMap[field.dataType] as any;
    }
    type = type || ('text' as any);
    const externalFieldKeyValue = ((field as any).externalFieldKey ?? '').toString().trim();
    const isExternalFieldKeyLocked = externalFieldKeyValue.length > 0;

    const position = field.position != null ? Number(field.position) : null;

    const actualFieldName = field.fieldName || field.name;
    const actualLabel = field?.fieldLabel || field?.labels;
    const actualIsRequired = field?.isRequired || field?.required || false;
    const actualIsMandatory = !!field?.isTypeMandatory;
    const actualIsVisible = field?.isVisible !== undefined ? field.isVisible : (field?.statusId === 1);

    const fg = this.fb.group({
      fieldName: [
        { value: actualFieldName || UF.default, disabled: !!actualFieldName },
        [Validators.required, Validators.pattern(UF.pattern), this.uniqueNameValidator.bind(this)],
      ],
      externalFieldKey: [
        { value: externalFieldKeyValue, disabled: isExternalFieldKeyLocked },
        this.isErpIntegrationActive ? [Validators.required] : [],
      ],
      fieldLabel: this.fb.group({
        master: [actualLabel?.master || '', [Validators.required]],
        report: [actualLabel?.report || '', [Validators.required]],
        mobile: [actualLabel?.mobile || '', [Validators.required]],
      }),
      fieldType: [{ value: type, disabled: actualIsMandatory }, [Validators.required]],
      isRequired: [actualIsRequired],
      isTypeMandatory: [{ value: actualIsMandatory, disabled: actualIsMandatory }],
      isVisible: [actualIsVisible !== false],
      defaultValue: [field?.defaultValue ?? (type === 'multiselect' ? [] : '')],
      position: [position, [Validators.required, Validators.min(1)]],
      groupId: [field?.groupId ?? null],
      maxLength: [field?.maxLength || null],
      minLength: [field?.minLength || null, [this.minLengthRangeValidator.bind(this)]],
      caseConversion: [field?.caseConversion ?? 'NONE'],
      trimSpaces: [!!field?.trimSpaces],
      mask: [field?.mask ?? ''],
      subFields: [field?.subFields || []],
      allowedExtensions: [field?.allowedExtensions || null],
      maxFileSize: [field?.maxFileSize || 5],
      multiple: [!!field?.multiple],
      valuesData: [field.valuesData ?? []],
      parentFieldName: [field.parentFieldName ?? ''],
      schemaMaster: [field.schemaMaster ?? ''],
      attachments: [field.attachments ?? []],
    });

    fg.get('groupId')?.valueChanges.subscribe(() => this.revalidatePositions());
    fg.get('position')?.valueChanges.subscribe(() => this.revalidatePositions());
    fg.get('maxLength')?.valueChanges.subscribe(() => {
      fg.get('minLength')?.updateValueAndValidity({ emitEvent: false });
    });
    return fg;
  }

  onGroupSelected(fieldGroup: FormGroup, groupId: string | null): void {
    fillNextFreePosition(fieldGroup, groupId);
  }

  private revalidatePositions() {
    const entity = this.entities[this.selectedEntityIndex];
    const array = this.getFieldsArray(entity);

    array.controls.forEach((ctrl) => {
      ctrl.get('position')?.updateValueAndValidity({
        emitEvent: false,
        onlySelf: true,
      });
    });
  }

  minLengthRangeValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;
    const min = control.value;
    const max = formGroup.get('maxLength')?.value;
    if (min === null || min === '' || max === null || max === '') return null;
    return Number(min) > Number(max) ? { minGreaterThanMax: true } : null;
  }

  private findPositionConflicts(fieldGroup: FormGroup, fieldsArray: FormArray): FormGroup[] {
    const currentGroup = fieldGroup.get('groupId')?.value ?? '';
    const currentPos = fieldGroup.get('position')?.value;
    if (currentPos === null || currentPos === '') return [];
    return fieldsArray.controls.filter(
      (ctrl) =>
        ctrl !== fieldGroup &&
        (ctrl.get('groupId')?.value ?? '') === currentGroup &&
        Number(ctrl.get('position')?.value) === Number(currentPos)
    ) as FormGroup[];
  }

  private shiftPositionsFrom(fieldGroup: FormGroup, fieldsArray: FormArray): void {
    const currentGroup = fieldGroup.get('groupId')?.value ?? '';
    const fromPosition = Number(fieldGroup.get('position')?.value);
    fieldsArray.controls.forEach((ctrl) => {
      if (ctrl === fieldGroup) return;
      if ((ctrl.get('groupId')?.value ?? '') !== currentGroup) return;
      const position = Number(ctrl.get('position')?.value) || 0;
      if (position < fromPosition) return;
      ctrl.get('position')?.setValue(position + 1, { emitEvent: false });
    });
  }

  getPositionOccupantLabel(fieldGroup: FormGroup): string | null {
    const entity = this.entities[this.selectedEntityIndex];
    const occupants = this.findPositionConflicts(fieldGroup, this.getFieldsArray(entity));
    if (!occupants.length) return null;
    return occupants.map((ctrl) => this.describeFieldGroup(ctrl, 0)).join(', ');
  }

  private describeFieldGroup(fieldGroup: FormGroup, index: number): string {
    return (
      fieldGroup.get('fieldLabel')?.get('master')?.value ||
      fieldGroup.get('fieldName')?.value ||
      `Campo #${index + 1}`
    );
  }

  private buildPositionShiftMessage(occupants: FormGroup[], position: number): string {
    const names = occupants.map((ctrl) => `"${this.describeFieldGroup(ctrl, 0)}"`).join(', ');
    return (
      `La posición ${position} ya está ocupada por ${names}.

` +
      'Si continúas, ese campo y los siguientes de su grupo se recorrerán una posición ' +
      'para dejar el orden solicitado.'
    );
  }

  private controlLabels: Record<string, string> = {
    fieldName: 'Nombre',
    fieldLabel: 'Etiquetas',
    externalFieldKey: 'Clave externa',
    fieldType: 'Tipo',
    position: 'Posición',
    groupId: 'Grupo',
    minLength: 'Mín Char',
    maxLength: 'Máx Char',
    defaultValue: 'Valor por defecto',
    schemaMaster: 'Schema',
  };

  private describeErrors(errors: Record<string, unknown> | null): string {
    if (!errors) return '';
    return Object.keys(errors)
      .map((key) => {
        switch (key) {
          case 'required':
            return 'es obligatorio';
          case 'pattern':
            return 'tiene un formato inválido';
          case 'duplicateName':
            return 'nombre duplicado';
          case 'minGreaterThanMax':
            return 'la longitud mínima supera la máxima';
          default:
            return key;
        }
      })
      .join(', ');
  }

  private collectInvalidFields(fields: AbstractControl[]): string[] {
    const details: string[] = [];
    fields.forEach((ctrl, index) => {
      const fieldGroup = ctrl as FormGroup;
      if (fieldGroup.valid) return;
      const fieldName = this.describeFieldGroup(fieldGroup, index);
      const invalidControls = Object.keys(fieldGroup.controls)
        .filter((controlName) => fieldGroup.get(controlName)?.invalid)
        .map((controlName) => {
          const label = this.controlLabels[controlName] ?? controlName;
          const reason = this.describeErrors(fieldGroup.get(controlName)?.errors);
          return reason ? `${label} (${reason})` : label;
        });
      details.push(`${fieldName}: ${invalidControls.join(', ')}`);
      console.warn('[user-fields-catalog] campo inválido', {
        index,
        fieldName,
        errors: Object.keys(fieldGroup.controls).reduce((acc, controlName) => {
          const control = fieldGroup.get(controlName);
          if (control?.invalid) acc[controlName] = control.errors;
          return acc;
        }, {} as Record<string, unknown>),
        rawValue: fieldGroup.getRawValue(),
      });
    });
    return details;
  }

  uniqueNameValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;
    const value = control.value?.trim().toLowerCase();
    if (!value) return null;
    const formArray = formGroup.parent as FormArray;
    if (!formArray) return null;

    const isDuplicate = formArray.controls.some((ctrl) => {
      return ctrl !== formGroup && ctrl.get('fieldName')?.value?.trim().toLowerCase() === value;
    });
    return isDuplicate ? { duplicateName: true } : null;
  }

  getFieldsArray(entityType: UserFieldEntityTypeEnum): FormArray {
    return this.formConfigs[entityType].get('fields') as FormArray;
  }

  getFieldControls(entityType: UserFieldEntityTypeEnum): FormGroup[] {
    return this.getFieldsArray(entityType).controls as FormGroup[];
  }

  confirmFieldEdit() {
    const entity = this.entities[this.selectedEntityIndex];
    const field = this.getFieldControls(entity)[this.editingFieldIndex];

    if (!field.valid) {
      field.markAllAsTouched();
      const details = this.collectInvalidFields([field]);
      this.handlerMessage.error({
        message: details.length
          ? `No se pudo guardar. Revisa: ${details.join(' | ')}`
          : 'Existen errores en el formulario',
      });
      return;
    }

    this.lockExternalFieldKey(field);
    this.revalidatePositions();

    const fieldsArray = this.getFieldsArray(entity);
    const occupants = this.findPositionConflicts(field, fieldsArray);

    if (!occupants.length) {
      this.saveEditedField(entity);
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        data: {
          title: 'Posición ocupada',
          message: this.buildPositionShiftMessage(occupants, Number(field.get('position')?.value)),
          confirmText: 'Recorrer y guardar',
          confirmColor: 'primary',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.shiftPositionsFrom(field, fieldsArray);
        this.revalidatePositions();
        this.saveEditedField(entity);
      });
  }

  private saveEditedField(entityType: UserFieldEntityTypeEnum): void {
    this.persistCatalog(entityType, {
      successMessage: 'Campo guardado con éxito',
      onSuccess: () => {
        this.dialogRefAdd.close(true);
        this.updateGridData();
      },
    });
  }

  get isSavingCatalogForSelectedEntity(): boolean {
    return this.savingCatalog.has(this.entities[this.selectedEntityIndex]);
  }

  private lockExternalFieldKey(fieldGroup: FormGroup) {
    const externalFieldKeyControl = fieldGroup.get('externalFieldKey');
    const externalFieldKey = externalFieldKeyControl?.value?.toString().trim();
    if (!externalFieldKeyControl || externalFieldKeyControl.disabled || !externalFieldKey) return;

    externalFieldKeyControl.setValue(externalFieldKey, { emitEvent: false });
    externalFieldKeyControl.disable({ emitEvent: false });
  }

  editField(index: number) {
    this.editingFieldIndex = index;
    const entity = this.entities[this.selectedEntityIndex];
    const field = this.getFieldControls(entity)[index];
    field.markAllAsTouched();

    const revertSnapshot = !this.isNewField ? field.getRawValue() : null;

    this.dialogRefAdd = this.dialog.open(this.addModal, {
      width: '95vw',
      maxWidth: '850px',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'custom-dialog-container',
    });

    this.dialogRefAdd.afterClosed().subscribe((result) => {
      if (!result && this.isNewField) {
        this.deleteFieldSilently(index);
      } else if (!result && !this.isNewField && revertSnapshot !== null) {
        field.patchValue(revertSnapshot, { emitEvent: false });
        field.markAsPristine();
        field.markAsUntouched();
        this.revalidatePositions();
        this.updateGridData();
      }
      this.isNewField = false;
    });
  }

  private deleteFieldSilently(index: number) {
    const entity = this.entities[this.selectedEntityIndex];
    this.getFieldsArray(entity).removeAt(index);
    this.updateGridData();
  }

  closeEditModal() {
    this.dialogRefAdd.close(false);
  }



  addNewField(entityType: UserFieldEntityTypeEnum) {
    const array = this.getFieldsArray(entityType);
    if (array.length >= this.maxFieldsPerEntity) {
      this.handlerMessage.warning({
        message: `Solo se permiten ${this.maxFieldsPerEntity} campos por entidad.`,
      });
      return;
    }

    this.isNewField = true;
    const newField = this.createFieldGroup();
    array.push(newField);
    fillNextFreePosition(newField, null);
    this.updateGridData();
    this.editField(array.length - 1);
  }

  isFieldLimitReached(entityType: UserFieldEntityTypeEnum): boolean {
    return this.getFieldsArray(entityType).length >= this.maxFieldsPerEntity;
  }

  deleteField(index: number) {
    const entity = this.entities[this.selectedEntityIndex];
    const fieldsArray = this.getFieldsArray(entity);
    const fieldGroup = fieldsArray.at(index) as FormGroup;

    const fieldLabel =
      fieldGroup.get('fieldLabel.master')?.value ||
      fieldGroup.get('fieldName')?.value ||
      'este campo';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmar eliminación',
        message: `¿Estás seguro de eliminar "${fieldLabel}"?`,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      if (this.dialogRefAdd) this.dialogRefAdd.close(true);
      fieldsArray.removeAt(index);
      this.updateGridData();
      this.editingFieldIndex = null;

      this.persistCatalog(entity, {
        successMessage: 'Campo eliminado',
        resyncOnError: true,
      });
    });
  }

  private persistCatalog(
    entityType: UserFieldEntityTypeEnum,
    options?: {
      successMessage?: string;
      onSuccess?: () => void;
      resyncOnError?: boolean;
    }
  ): void {
    if (this.savingCatalog.has(entityType)) return;

    const form = this.formConfigs[entityType];
    if (!form.valid) {
      form.markAllAsTouched();
      const details = this.collectInvalidFields(this.getFieldsArray(entityType).controls);
      this.handlerMessage.error({
        message: details.length
          ? `No se guardó la configuración porque ${details.length} campo(s) del catálogo tienen datos inválidos: ${details.join(' | ')}. Corrígelos o reporta este detalle a soporte.`
          : 'Existen errores en los campos',
      });
      return;
    }

    const rawValue = form.getRawValue();
    const { _id, ...rest } = rawValue;

    if (!this.assertCanPersistCatalog(_id ?? null)) {
      return;
    }

    const mappedFields = rest.fields.map((f, i) => {
      const ctrl = this.getFieldsArray(entityType).at(i) as FormGroup;
      let defaultValue = f.defaultValue;
      if (f.fieldType === 'multiselect' && !Array.isArray(defaultValue)) {
        defaultValue = defaultValue ? [defaultValue] : [];
      }
      if (f.fieldType === 'select' && Array.isArray(defaultValue)) {
        defaultValue = defaultValue[0] || '';
      }

      return {
        ...f,
        externalFieldKey: f.externalFieldKey?.trim() || undefined,
        defaultValue,
        name: f.fieldName,
        labels: f.fieldLabel,
        dataType: this.convertType(f.fieldType),
        required: f.isRequired,
        statusId: f.isVisible ? 1 : 0,
        position: f.position,
        groupId: f.groupId || null,
        maxLength: f.maxLength ? Number(f.maxLength) : null,
        minLength: f.minLength ? Number(f.minLength) : null,
      };
    });

    const payload: any = {
      ...rest,
      statusId: rest.statusId ? 1 : 0,
      fields: mappedFields,
      groups: rest.groups ?? [],
    };

    if (_id) {
      delete payload.companyId;
      delete payload.entityType;
    }

    this.savingCatalog.add(entityType);
    this.companyFieldCatalogService
      .saveFieldsMaster(payload as any, _id ? 'PATCH' : 'POST', _id)
      .subscribe({
        next: () => {
          this.savingCatalog.delete(entityType);
          this.handlerMessage.success({
            message: options?.successMessage ?? 'Configuración actualizada!',
          });
          this.loadAllConfigs();
          options?.onSuccess?.();
        },
        error: (err) => {
          this.savingCatalog.delete(entityType);
          this.handlerMessage.error({
            message: err?.error?.message || err.message,
          });
          if (options?.resyncOnError) this.loadAllConfigs();
        },
      });
  }

  private saveStatusConfig(entityType: UserFieldEntityTypeEnum) {
    if (this.autoSavingStatus.has(entityType)) return;

    const form = this.formConfigs[entityType];
    const rawValue = form.getRawValue();
    const { _id, ...rest } = rawValue;

    if (!this.assertCanPersistCatalog(_id ?? null)) {
      return;
    }

    this.autoSavingStatus.add(entityType);
    const statusId = rest.statusId ? 1 : 0;
    const payload = _id
      ? { statusId }
      : {
          companyId: rest.companyId,
          entityType: rest.entityType,
          statusId,
        };

    this.companyFieldCatalogService
      .saveFieldsMaster(payload as any, _id ? 'PATCH' : 'POST', _id)
      .subscribe({
        next: () => {
          this.autoSavingStatus.delete(entityType);
          this.handlerMessage.success({ message: 'Estado actualizado' });
          this.loadAllConfigs();
        },
        error: (err) => {
          this.autoSavingStatus.delete(entityType);
          this.handlerMessage.error({ message: err?.error?.message || err.message });
        },
      });
  }

  convertType(type: string): string {
    const types = {
      text: 'string',
      email: 'string',
      select: 'array',
      multiselect: 'object',
      form: 'string',
      attachment: 'string',
    };
    return types[type] || type;
  }

  getEntityLabel(entity: UserFieldEntityTypeEnum): string {
    return this.entityLabels[entity] ?? entity;
  }

  isConfigButtonDisabledBySchema(fieldGroup: FormGroup): boolean {
    const type = fieldGroup.get('fieldType')?.value;
    const hasSchema = !!fieldGroup.get('schemaMaster')?.value;
    return ['select', 'multiselect'].includes(type) && hasSchema;
  }

  openConfigModal(fieldGroup: FormGroup) {
    const type = fieldGroup.get('fieldType').value;
    if (type === 'attachment') {
      this.dialog
        .open(AttachmentConfigDialogComponent, {
          width: '650px',
          data: {
            attachments: fieldGroup.get('attachments').value || [],
            multiple: fieldGroup.get('multiple').value,
          },
        })
        .afterClosed()
        .subscribe((res) => {
          if (res) fieldGroup.patchValue({ attachments: res.attachments, multiple: res.multiple });
        });
    } else if (type === 'form') {
      this.dialog
        .open(UserSubfieldsConfigComponent, {
          width: '80%',
          data: {
            subFields: fieldGroup.get('subFields').value,
            multiple: fieldGroup.get('multiple').value,
          },
        })
        .afterClosed()
        .subscribe(
          (res) =>
            res && fieldGroup.patchValue({ subFields: res.subFields, multiple: res.multiple })
        );
    } else {
      this.dialog
        .open(ValuesModalComponent, {
          width: '500px',
          data: {
            values: Array.isArray(fieldGroup.get('valuesData').value)
              ? fieldGroup.get('valuesData').value
              : [],
          },
        })
        .afterClosed()
        .subscribe((res) => res && fieldGroup.get('valuesData').setValue(res));
    }
  }

  private normalizeGroups(groups: any[]): FieldGroup[] {
    const normalized: FieldGroup[] = (groups || []).map((group) => ({
      _id: group._id,
      name: group.name,
      position: group.position ?? 0,
      icon: group.icon ?? null,
      iconColor: normalizeIconColor(group.iconColor),
    }));
    return normalizeGroupPositions(normalized);
  }

  manageGroups() {
    const entityType = this.entities[this.selectedEntityIndex];
    const form = this.formConfigs[entityType];
    const fieldsArray = this.getFieldsArray(entityType);

    const fieldCountByGroupId: Record<string, number> = {};
    fieldsArray.controls.forEach((ctrl) => {
      const groupId = ctrl.get('groupId')?.value;
      if (groupId) fieldCountByGroupId[groupId] = (fieldCountByGroupId[groupId] ?? 0) + 1;
    });

    this.dialog
      .open(FieldGroupsDialogComponent, {
        width: '95vw',
        maxWidth: '1280px',
        disableClose: true,
        panelClass: ['custom-dialog-container', 'responsive-modal'],
        data: { groups: this.currentGroups, fieldCountByGroupId },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;

        form.get('groups')?.setValue(result.groups);

        fieldsArray.controls.forEach((ctrl) => {
          if (result.removedGroupIds.includes(ctrl.get('groupId')?.value)) {
            ctrl.get('groupId')?.setValue(null, { emitEvent: false });
          }
        });

        this.updateGridData();
      });
  }
}
